import type { SmsIntegration } from '@chukua/common';
import { Alert, Button, Popconfirm, Tag } from 'antd';
import { Plus } from 'lucide-react';
import { Link } from 'react-router';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { IntegrationLogo } from '../../../components/IntegrationLogo';
import { addIntegrationPath, INTEGRATION_KIND_LABELS } from '../../../integrations.paths';
import type { useSmsIntegrations } from '../hooks/use-sms-integrations';
import { installableProviders, sendingState } from '../../../policies/messaging-provider.policy';
import { smsIntegrationFacts } from '../policies/sms-sending.policy';
import { smsIntegrationPath } from '../sms.paths';

type Sms = ReturnType<typeof useSmsIntegrations>;

/** The installed SMS providers, which one sends, and switching between them. */
export function SmsSection({ sms }: { sms: Sms }) {
  return (
    <section aria-labelledby="sms-integrations" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 id="sms-integrations" className="sr-only">
          {INTEGRATION_KIND_LABELS.sms}
        </h2>
        <span className="text-sm text-muted">Driver sign-in codes and app invites. One provider sends at a time.</span>
      </div>
      <SmsSectionBody sms={sms} />
    </section>
  );
}

function SmsSectionBody({ sms }: { sms: Sms }) {
  const { state } = sms;
  if (state.status === 'loading') return <RowListSkeleton label="Loading SMS providers" rows={1} />;
  if (state.status !== 'loaded') return <Alert type="error" showIcon title="Couldn't load the SMS providers. Try reloading the page." />;

  const sending = sendingState(state.integrations);
  const canAdd = installableProviders(state.providers, state.integrations).length > 0;
  return (
    <>
      {sending.kind !== 'sending' && (
        <Alert
          type="warning"
          showIcon
          title="No SMS provider is sending"
          description={`Drivers can't sign in to the app and app invites can't be sent. ${sending.kind === 'none-installed' ? 'Add a provider with your company’s account.' : 'Turn one on below.'}`}
        />
      )}
      {state.integrations.length > 0 && (
        <ul className="divide-y divide-line rounded-xl border border-line bg-surface">
          {state.integrations.map((integration) => (
            <SmsIntegrationRow
              key={integration.id}
              integration={integration}
              sending={sending.kind === 'sending' ? sending.integration : undefined}
              sms={sms}
            />
          ))}
        </ul>
      )}
      {canAdd && (
        <Link
          to={addIntegrationPath('sms')}
          className="flex items-center gap-2 rounded-xl border border-dashed border-line px-4 py-3 font-medium text-link hover:border-primary"
        >
          <Plus className="size-4" aria-hidden />
          {state.integrations.length > 0 ? 'Add another SMS provider' : 'Add an SMS provider'}
        </Link>
      )}
    </>
  );
}

interface SmsIntegrationRowProps {
  integration: SmsIntegration;
  /** The installation sending now, if any, named when switching away from it. */
  sending: SmsIntegration | undefined;
  sms: Sms;
}

function SmsIntegrationRow({ integration, sending, sms }: SmsIntegrationRowProps) {
  const provider = sms.state.status === 'loaded' ? sms.state.providers.find((candidate) => candidate.key === integration.providerKey) : undefined;
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5">
      {provider && <IntegrationLogo logoSvg={provider.logoSvg} name={provider.displayName} />}
      <div className="min-w-0 flex-1 basis-56">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={smsIntegrationPath(integration.id)} className="font-semibold text-fg hover:text-link">
            {integration.displayName}
          </Link>
          {integration.isSending ? <Tag color="success">Sending texts</Tag> : <Tag>Off</Tag>}
        </div>
        <p className="flex flex-wrap gap-x-3 text-sm text-muted">
          {smsIntegrationFacts(integration).map((fact) => (
            <span key={fact}>{fact}</span>
          ))}
        </p>
      </div>
      <div className="flex gap-2">
        {!integration.isSending && (
          <Popconfirm
            title={sending ? `Switch texts to ${integration.displayName}?` : `Turn on ${integration.displayName}?`}
            description={
              sending
                ? `Sign-in codes and invites will go out through ${integration.displayName} instead of ${sending.displayName}. A code already sent stays valid.`
                : `Sign-in codes and invites will go out through ${integration.displayName}.`
            }
            okText={sending ? 'Switch' : 'Turn on'}
            onConfirm={() => sms.startSending(integration)}
          >
            <Button type="primary" size="small">
              {sending ? 'Switch to this' : 'Turn on'}
            </Button>
          </Popconfirm>
        )}
        <Link to={smsIntegrationPath(integration.id)}>
          <Button size="small">Settings</Button>
        </Link>
      </div>
    </li>
  );
}
